import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Target,
  TrendingUp,
  Award,
  Users,
  ChartBar,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { cn } from '@/lib/utils';
import api from '@/utils/ky';
import ExpiredLinkMessage from '@/components/ExpiredLinkMessage';
import ExpiryLinkTimer from '@/components/ExpiryLinkTimer';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@mui/material';

const features = [
  {
    icon: TrendingUp,
    title: 'Master Complex Sales',
    subtitle: 'Insurance & Financial Products',
    description:
      'Practice proven techniques for high-value product sales, objection handling, and needs analysis.',
    stats: '35% Higher Close Rates',
    image: 'https://tenorhq.com/wp-content/uploads/2024/03/sales_feedback.png',
  },
  {
    icon: Users,
    title: 'Client Simulation',
    subtitle: 'AI-Powered Practice',
    description:
      'Train with ultra-realistic customer scenarios based on real conversations from top performers.',
    stats: '10,000+ Practice Sessions',
    image: 'https://tenorhq.com/wp-content/uploads/2024/03/custom_scenario.png',
  },
  {
    icon: Award,
    title: 'Expert Coaching',
    subtitle: 'Real-Time Guidance',
    description:
      'Get personalized feedback and actionable insights from AI trained on top 1% of salespeople.',
    stats: 'Used by Industry Leaders',
    image:
      'https://tenorhq.com/wp-content/uploads/2024/03/practice_conversation.png',
  },
];

const companies = [
  [
    { name: 'KPMG', src: '/teams/kpmg.svg', className: 'h-[26px]' },
    { name: 'Grab', src: '/teams/grab.svg', className: 'h-[21px]' },
    {
      name: 'Cathay Pacific',
      src: '/teams/cathay_pacific.svg',
      className: 'h-[21px]',
    },
    { name: 'Sephora', src: '/teams/sephora.svg', className: 'h-[16px]' },
    { name: 'Fujifilm', src: '/teams/fujifilm.svg', className: 'h-[16px]' },
    { name: 'BAT', src: '/teams/bat.svg', className: 'h-[20px]' },
    { name: 'Wilmar', src: '/teams/wilmar.svg', className: 'h-[20px]' },
    { name: 'BCG', src: '/teams/bcg.svg', className: 'h-[20px]' },
  ],
  [
    {
      name: 'Commonwealth Bank',
      src: '/teams/commonwealth_bank.svg',
      className: 'h-[25px]',
    },
    { name: 'Kredivo', src: '/teams/kredivo.svg', className: 'h-[22px]' },
    { name: 'COTY', src: '/teams/coty.svg', className: 'h-[23px]' },
    { name: 'FAB', src: '/teams/fab.svg', className: 'h-[27px]' },
    {
      name: 'Zurich Insurance',
      src: '/teams/zurich.svg',
      className: 'h-[22px]',
    },
    { name: 'QBE', src: '/teams/qbe.svg', className: 'h-[24px]' },
    { name: 'Coupang', src: '/teams/coupang.svg', className: 'h-[24px]' },
    { name: 'SPH', src: '/teams/sph.svg', className: 'h-[28px]' },
  ],
];

export default function SalesLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const posthog = usePostHog();

  const { isFetched: isCheckCodeQueryFetched, data } = useQuery({
    queryKey: ['sales', 'check-code'],
    enabled: searchParams.has('code'),
    placeholderData: {
      isValid: false,
      validityInMinutes: 0,
      isExpired: true,
    },
    queryFn() {
      return api
        .get('api/public/verify', {
          searchParams: { code: searchParams.get('code') },
        })
        .json<{
          isValid: boolean;
          error?: string;
          validityInMinutes: number;
          expiresAt?: number;
          isExpired?: boolean;
        }>();
    },
  });

  const handleStartTrial = () => {
    if (!isCheckCodeQueryFetched || !data.isValid) return;

    posthog.capture('sales_start_trial_clicked', {
      code: searchParams.get('code'),
      validityInMinutes: data.validityInMinutes,
    });

    navigate(`/sales/categories?${searchParams.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Access check UI */}
      {searchParams.has('code') && isCheckCodeQueryFetched && (
        <>
          {data.isExpired ? (
            <ExpiredLinkMessage />
          ) : (
            data?.expiresAt &&
            data.expiresAt - Date.now() < 6 * 60 * 60 * 100 && (
              <ExpiryLinkTimer sessionEndsAt={data.expiresAt} />
            )
          )}
        </>
      )}

      {/* Hero Section */}
      <div className="relative px-6 pt-20 lg:pt-32 lg:px-8 pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/2 w-[1000px] h-[1000px] bg-gradient-to-b from-blue-500/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/2 w-[800px] h-[800px] bg-gradient-to-t from-blue-50/30 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="https://coach.hupo.co/logos/trademarked/orange/logo.svg"
              alt="AI Sales Coach"
              className="h-16 w-auto mx-auto mb-12"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <h1 className="text-5xl lg:text-7xl font-bold tracking-tight bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 bg-clip-text text-transparent">
              Your AI Sales Coach
              <br />
              For Complex Products
            </h1>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Practice high-stakes sales conversations, master objection
              handling, and get expert feedback to close more deals.
            </p>

            {!isCheckCodeQueryFetched || !data.isValid ? null : (
              <Button
                onClick={handleStartTrial}
                size="lg"
                className="px-8 py-6 text-lg bg-blue-600 hover:bg-blue-700 rounded-full transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-blue-500/20"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </motion.div>
        </div>

        {/* Demo Video */}
        <div className="relative mx-auto mt-16 max-w-4xl">
          <div className="overflow-hidden rounded-2xl shadow-2xl">
            <video
              src="https://dl1186a8rusj2.cloudfront.net/videos/compressed_Hero.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Trusted By Section */}
      <div className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6">
          <p className="text-center text-base font-medium text-gray-600 mb-12">
            Trusted by 100+ of APAC's top organizations
          </p>

          <div className="space-y-12">
            {companies.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className="grid grid-cols-4 gap-8 md:grid-cols-8"
              >
                {group.map((company) => (
                  <div
                    key={company.name}
                    className="flex items-center justify-center"
                  >
                    <img
                      src={'https://hupo.co' + company.src}
                      alt={company.name}
                      className={cn(
                        'grayscale hover:grayscale-0 transition-all duration-300',
                        company.className,
                      )}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-black/0 to-black/60" />
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 left-4 text-white">
                      <feature.icon className="h-6 w-6 mb-2" />
                      <p className="text-sm font-medium">{feature.stats}</p>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-blue-600 mb-3">
                      {feature.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
